<template>
  <b-card>
    <b-row>
      <b-col
        cols="12"
        md="6"
      >
        <b-form-group :label="$t('partner-admin.fields.name')">
          <b-form-input
            :value="form.name"
            :disabled="loading"
            @input="value => updateField('name', value)"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
      >
        <b-form-group :label="$t('partner-admin.fields.code')">
          <b-form-input
            :value="form.code"
            :disabled="loading || !createMode"
            @input="value => updateField('code', value)"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
      >
        <b-form-group :label="$t('partner-admin.fields.allowed-order-types')">
          <v-select
            :value="filteredAllowedOrderTypes"
            :disabled="loading"
            :options="allowedOrderTypeOptions"
            :reduce="option => option.value"
            multiple
            :close-on-select="false"
            :placeholder="$t('partner-admin.fields.allowed-order-types-placeholder')"
            @input="updateAllowedOrderTypes"
          >
            <template #selected-option="option">
              {{ option.label }}
            </template>
            <template #option="option">
              {{ option.label }}
            </template>
          </v-select>
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
      >
        <b-form-group :label="$t('partner-admin.fields.status')">
          <b-form-checkbox
            :checked="form.status === 'active'"
            :disabled="loading"
            switch
            @input="updateStatusToggle"
          >
            {{ form.status === 'active' ? $t('partner-admin.statuses.active') : $t('partner-admin.statuses.blocked') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <text-editor
          id="partner-order-request-instruction"
          :label="$t('partner-admin.fields.order-request-instruction')"
          :value="orderRequestInstruction"
          :variant="'simple'"
          @input="updateOrderRequestInstruction"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BRow,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import TextEditor from '@/views/_global/TextEditor.vue'

const defaultAllowedOrderTypes = [
  'specialized_transport',
  'basic_transport',
  'sanitary_transport',
  'medical_courier',
  'event',
]

const hiddenAllowedOrderTypes = [
  'multi_transport',
]

export default {
  name: 'ExternalPartnerForm',
  components: {
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BRow,
    vSelect,
    TextEditor,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    createMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    form() {
      return this.value
    },
    visibleAllowedOrderTypes() {
      return defaultAllowedOrderTypes.filter(type => !hiddenAllowedOrderTypes.includes(type))
    },
    filteredAllowedOrderTypes() {
      return this.sanitizeAllowedOrderTypes(this.form.allowedOrderTypes)
    },
    allowedOrderTypeOptions() {
      return this.visibleAllowedOrderTypes.map(type => ({
        value: type,
        label: this.resolveOrderTypeLabel(type),
      }))
    },
    orderRequestInstruction() {
      return typeof this.form.orderRequestInstruction === 'string'
        ? this.form.orderRequestInstruction
        : ''
    },
  },
  mounted() {
    this.ensureAllowedOrderTypesSanitized()
  },
  methods: {
    updateField(field, value) {
      this.$emit('input', {
        ...this.form,
        [field]: value,
      })
    },
    updateStatusToggle(isActive) {
      this.updateField('status', isActive ? 'active' : 'blocked')
    },
    updateAllowedOrderTypes(values) {
      this.updateField('allowedOrderTypes', this.sanitizeAllowedOrderTypes(values))
    },
    updateOrderRequestInstruction(value) {
      this.updateField('orderRequestInstruction', value || '')
    },
    sanitizeAllowedOrderTypes(values) {
      const normalized = Array.isArray(values) ? values : []
      const allowedSet = new Set(this.visibleAllowedOrderTypes)

      return normalized.filter(type => allowedSet.has(type))
    },
    ensureAllowedOrderTypesSanitized() {
      const current = Array.isArray(this.form.allowedOrderTypes) ? this.form.allowedOrderTypes : []
      const sanitized = this.sanitizeAllowedOrderTypes(current)
      const isSame = current.length === sanitized.length && current.every((value, idx) => value === sanitized[idx])

      if (!isSame) {
        this.updateField('allowedOrderTypes', sanitized)
      }
    },
    resolveOrderTypeLabel(type) {
      const i18nKey = `order.types.${type}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return type
    },
  },
}
</script>
